import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Search, TrendingUp, Layout, Link2, BarChart } from 'lucide-react';


const SEO = () => {
  const quoteRef = useRef(null);
  const contentRef = useRef(null);
  const processRef = useRef(null);
  
  const isQuoteInView = useInView(quoteRef, { once: true, margin: "-100px" });
  const isContentInView = useInView(contentRef, { once: true, margin: "-100px" });
  const isProcessInView = useInView(processRef, { once: true, margin: "-100px" });

  const processSteps = [
    {
      title: "Audit & Research",
      description: "We begin by auditing your current site performance, researching competitor strategies, and identifying valuable keywords. Understanding your niche allows us to craft a targeted approach.",
      icon: Search
    },
    {
      title: "On-Page Optimization",
      description: "Ensuring that the content, metadata, and technical aspects of your website align with SEO best practices. We focus on keyword density, site speed, and mobile-friendliness.",
      icon: Layout
    },
    {
      title: "Content Strategy",
      description: "Developing keyword-rich, engaging content tailored to your audience that not only ranks but also drives conversions.",
      icon: TrendingUp
    },
    {
      title: "Link Building",
      description: "Building a network of backlinks to boost your domain authority and improve ranking potential.",
      icon: Link2
    },
    {
      title: "Performance Tracking",
      description: "Continuous analysis of rankings, traffic, and bounce rates to adjust strategies for better performance.",
      icon: BarChart
    }
  ];

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        {/* Breadcrumb */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-8 text-gray-600"
        >
          
        </motion.div>

        {/* Quote Section */}
        <motion.div
          ref={quoteRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isQuoteInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-[#f4b400] bg-clip-text text-transparent">
            SEO
          </h1>
          <p className="text-xl md:text-2xl italic text-gray-600">
            "Play by the rules, but be ferocious."
            <span className="block mt-2 text-base">— Phil Knight</span>
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isContentInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16 text-gray-700"
        >
          <p className="text-lg leading-relaxed mb-8">
            Most agencies chase the obvious keywords. We go deeper. We find the unexploited angles, the hidden opportunities that your competitors overlook. We get you found on Google, Bing, and even search engines your competition hasn't tapped yet. Our SEO doesn't just drive clicks—it drives <span className="font-bold text-[#f4b400]">revenue</span>.
          </p>
        </motion.div>

        {/* Process Section */}
        <motion.div
          ref={processRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-gray-800">Process</h2>
          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                animate={isProcessInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  <step.icon className="w-6 h-6 text-[#ffd54f]" />
                  <h3 className="text-xl font-semibold text-[#ffd54f]">{step.title}</h3>
                </div>
                <p className="text-gray-600 mt-2">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="p-8 bg-white/80 backdrop-blur-sm rounded-2xl">
            <p className="text-lg text-gray-700 mb-6">
              SEO is the foundation that <span className="font-bold text-[#f4b400]">ignites</span> your online visibility, bringing organic traffic to your website and building long-term authority.
            </p>
            <p className="text-2xl font-bold text-gray-800">
              Ready to plant the seeds of growth? Let's chat!
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SEO;